// sim/ecosystemEvents.ts — the DYNAMIC ECOSYSTEM EVENT scheduler + effect system.
//
// A world-scale disturbance layer for the ECOSYSTEM world. On a deterministic
// cadence it STARTS one of five events (weighted pick), holds it for a bounded
// duration, then ENDS it — flipping ecosystem-wide MODIFIERS for the duration and
// firing a one-shot onset effect:
//
//   PesticideOutbreak  ↓food-regen, ↓capacity, ↓gene-drops, +drain, culls 40%
//   HeatWave           ↓↓food-regen, ↓capacity, +drain (thins weak organisms)
//   Flooding           ↓↓↓food-regen, ↓capacity, +drain, culls 25%
//   PredatorMigration  ↑↑capacity, ↑gene-drops, bursts 6 predators
//   FoodSurplusBloom   ↑↑food-regen, ↑capacity, ↑gene-drops, blooms food nodes
//
// THE MODIFIER SEAM. The system WRITES a single mutable EcosystemModifiers struct
// that other systems (resources / ecosystem / organisms / castes) READ. Each of
// those systems treats `mods === undefined` as neutral, so an events-OFF world is
// byte-identical. The struct decouples the event system from those systems — no
// circular construction — and is the only state the event system mutates outside
// its own counters.
//
// DETERMINISM: the event system draws ONLY from its OWN isolated Rng (seeded
// independently of the colony + wildlife streams), so adding events never
// perturbs an existing stream. Pure integer state; fixed iteration; the cull/
// burst loops reuse a preallocated scratch and allocate nothing per tick.

import { ECO_EVENT, ECO_EVENT_COUNT, KIND, NIL, SPECIES, SPECIES_COUNT } from '../types.ts';
import type {
  EcoEvent,
  EcosystemEventConfig,
  EcosystemEventEffect,
  EntityId,
  Species,
  System,
  Tick,
} from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { WildlifeSystem } from './wildlife.ts';
import type { ResourceSystem } from './resources.ts';

// ---------------------------------------------------------------------------
// THE MODIFIER SEAM — one mutable struct the event system writes + systems read.
// ---------------------------------------------------------------------------

/**
 * Ecosystem-wide modifiers an active event imposes. Other systems READ these and
 * treat an absent struct (or the neutral values below) as "no event": a percent
 * is /100-scaled (100 = unchanged), drain is an absolute energy loss (0 = none).
 */
export interface EcosystemModifiers {
  /** Resources: regenAmount is scaled by /100 (neutral 100). */
  foodRegenPct: number;
  /** Ecosystem: carryingCapacity is scaled by /100 (neutral 100). */
  wildlifeCapacityPct: number;
  /** Gene drops: drop chance is scaled by /100 (neutral 100). */
  geneDropPct: number;
  /** Organisms: extra energy lost per tick (neutral 0). */
  organismDrain: number;
}

/** A fresh neutral-modifier struct (no event active). */
export function neutralModifiers(): EcosystemModifiers {
  return { foodRegenPct: 100, wildlifeCapacityPct: 100, geneDropPct: 100, organismDrain: 0 };
}

// ---------------------------------------------------------------------------
// Effect table — one row per ECO_EVENT.* (index 0 = None). The first four
// fields are the persistent modifiers (copied into `mods` while active); the
// last three are one-shot ONSET effects applied when the event starts:
//   cullPct        kill wildlife at onset — by default the first
//                  floor(totalPopulation * cullPct / 100) in active-set order, or
//                  (config.proportionalCull) floor(speciesCount * cullPct / 100)
//                  of EACH species, a representative die-off (see cullWildlife)
//   predatorBurst  spawn N predators (Rat/Bird cycle) at onset
//   bloomAmount    add N to every on-grid food node at onset
// ---------------------------------------------------------------------------

/**
 * One effect row per ECO_EVENT.* (None first). Re-exported as the canonical
 * EcosystemEventEffect (declared dependency-free in types.ts) so a world can
 * override the magnitudes via EcosystemEventConfig.effects without editing this
 * module (see the constructor). The order is a HARD CONTRACT: index === ECO_EVENT.* id.
 */
export type EventEffect = EcosystemEventEffect;

/**
 * The DEFAULT effect table, indexed by ECO_EVENT.* — order is a HARD CONTRACT
 * (None first). A world that leaves EcosystemEventConfig.effects undefined reads
 * THIS table verbatim (byte-identical behaviour); a provided table replaces it.
 */
export const EVENT_EFFECTS: readonly EventEffect[] = [
  // None — neutral, no onset effect.
  { foodRegenPct: 100, wildlifeCapacityPct: 100, geneDropPct: 100, organismDrain: 0, cullPct: 0, predatorBurst: 0, bloomAmount: 0 },
  // PesticideOutbreak — poisons the field: less food, fewer predators + a cull, weak drops, organisms drain.
  { foodRegenPct: 100, wildlifeCapacityPct: 60, geneDropPct: 40, organismDrain: 1, cullPct: 40, predatorBurst: 0, bloomAmount: 0 },
  // HeatWave — scorches the food supply + drains organisms; mild predator dip.
  { foodRegenPct: 50, wildlifeCapacityPct: 80, geneDropPct: 100, organismDrain: 1, cullPct: 0, predatorBurst: 0, bloomAmount: 0 },
  // Flooding — washes out food, drains organisms, drowns some wildlife.
  { foodRegenPct: 40, wildlifeCapacityPct: 80, geneDropPct: 100, organismDrain: 1, cullPct: 25, predatorBurst: 0, bloomAmount: 0 },
  // PredatorMigration — a predator influx: higher ceiling, richer drops, a burst spawn.
  { foodRegenPct: 100, wildlifeCapacityPct: 160, geneDropPct: 130, organismDrain: 0, cullPct: 0, predatorBurst: 6, bloomAmount: 0 },
  // FoodSurplusBloom — abundance: food surges, more wildlife, richer drops, a food bloom.
  { foodRegenPct: 160, wildlifeCapacityPct: 120, geneDropPct: 130, organismDrain: 0, cullPct: 0, predatorBurst: 0, bloomAmount: 8 },
];

/**
 * The largest predator burst any single event spawns at onset. The scheduler runs
 * ONE non-overlapping event at a time, so an ecosystem world reserves entity
 * capacity for exactly one burst. Deriving it from EVENT_EFFECTS (instead of the
 * caller hardcoding a number) keeps that reserve correct if a `predatorBurst` is
 * ever retuned — a raised burst can't silently outgrow the headroom.
 */
export const MAX_PREDATOR_BURST: number = EVENT_EFFECTS.reduce(
  (m, e) => (e.predatorBurst > m ? e.predatorBurst : m),
  0,
);

/** Predators cycled by a PredatorMigration burst (the colony-pressuring species). */
const BURST_CYCLE: readonly Species[] = [SPECIES.Rat, SPECIES.Bird];

// ---------------------------------------------------------------------------
// The system.
// ---------------------------------------------------------------------------

export class EcosystemEventSystem implements System {
  readonly name = 'eco-events';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly wildlife: WildlifeSystem;
  private readonly resources: ResourceSystem;
  /** The shared modifier struct this system mutates (others read it). */
  private readonly mods: EcosystemModifiers;
  /** ISOLATED rng — never the colony/wildlife stream (determinism). */
  private readonly rng: Rng;
  private readonly config: EcosystemEventConfig;
  /**
   * The effect table this system reads. Resolved ONCE: config.effects when the
   * world overrides the magnitudes, else the module EVENT_EFFECTS. Absent ⇒ the
   * exact same reference as today, so behaviour + hash are byte-identical.
   */
  private readonly effects: readonly EventEffect[];
  /**
   * Onset-cull mode. When true the cull is SPECIES-PROPORTIONAL; absent/false ⇒
   * today's flat first-N (active-set order) cull, byte-identical to a pre-change
   * build. Resolved once from config.proportionalCull (defaults to false).
   */
  private readonly proportionalCull: boolean;

  // --- Scheduler state (all integer). ---
  /** The currently active event (ECO_EVENT.None when idle). */
  private active: EcoEvent = ECO_EVENT.None;
  /** Ticks the active event has left (counts down to 0 → end). */
  private remaining = 0;
  /** Ticks until the next event starts (counts down while idle). */
  private ticksToNext: number;
  /** Cursor into BURST_CYCLE (deterministic predator rotation). */
  private burstCursor = 0;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  eventsFiredTotal = 0;
  /** Times each real event fired, indexed [0, ECO_EVENT_COUNT) (Pesticide..Bloom). */
  readonly firedByType: Int32Array;

  /** Reused snapshot for the onset cull (despawn-safe, allocation-free). */
  private readonly scratch: Int32Array;
  /** Per-species kill targets for the proportional onset cull (fixed-size scratch). */
  private readonly cullQuota: Int32Array;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    wildlife: WildlifeSystem,
    resources: ResourceSystem,
    mods: EcosystemModifiers,
    rng: Rng,
    config: EcosystemEventConfig,
  ) {
    this.entities = entities;
    this.grid = grid;
    this.wildlife = wildlife;
    this.resources = resources;
    this.mods = mods;
    this.rng = rng;
    this.config = config;
    // Override the effect magnitudes only when the world provides a table; absent
    // ⇒ the module default (same reference), so behaviour + hash are unchanged.
    this.effects = config.effects ?? EVENT_EFFECTS;
    // Proportional cull is OPT-IN; absent/false ⇒ the flat first-N cull (unchanged).
    this.proportionalCull = config.proportionalCull === true;
    this.firedByType = new Int32Array(ECO_EVENT_COUNT);
    this.scratch = new Int32Array(entities.capacity);
    this.cullQuota = new Int32Array(SPECIES_COUNT);
    // First event lands after an initial unpredictable gap.
    this.ticksToNext = config.minGap + this.rng.int(config.gapJitter);
  }

  // --- Per-tick: run the scheduler --------------------------------------------

  update(_tick: Tick): void {
    if (this.active !== ECO_EVENT.None) {
      // An event is active: count down, end on expiry (restore neutral mods).
      this.remaining--;
      if (this.remaining <= 0) {
        this.endEvent();
      }
      return;
    }
    // Idle: count down to the next event, then start one.
    this.ticksToNext--;
    if (this.ticksToNext <= 0) {
      this.startEvent();
    }
  }

  /** Restore neutral modifiers and re-arm the next-event countdown. */
  private endEvent(): void {
    const m = this.mods;
    m.foodRegenPct = 100;
    m.wildlifeCapacityPct = 100;
    m.geneDropPct = 100;
    m.organismDrain = 0;
    this.active = ECO_EVENT.None;
    this.ticksToNext = this.config.minGap + this.rng.int(this.config.gapJitter);
  }

  /** Pick + begin an event: write its modifiers, fire its onset effect, count it. */
  private startEvent(): void {
    const ev = this.pickEvent();
    this.active = ev;
    this.remaining = this.config.minDuration + this.rng.int(this.config.durationJitter);

    const fx = this.effects[ev] as EventEffect;
    const m = this.mods;
    m.foodRegenPct = fx.foodRegenPct;
    m.wildlifeCapacityPct = fx.wildlifeCapacityPct;
    m.geneDropPct = fx.geneDropPct;
    m.organismDrain = fx.organismDrain;

    if (fx.cullPct > 0) this.cullWildlife(fx.cullPct);
    if (fx.predatorBurst > 0) this.burstPredators(fx.predatorBurst);
    if (fx.bloomAmount > 0) this.resources.bloom(fx.bloomAmount);

    this.eventsFiredTotal++;
    // firedByType is indexed by real-event ordinal (event id - 1).
    const idx = (ev as number) - 1;
    this.firedByType[idx] = (this.firedByType[idx] as number) + 1;
  }

  /**
   * Weighted pick of a real event over config.weights (length ECO_EVENT_COUNT,
   * order Pesticide..Bloom). Standard sum-then-walk over the isolated rng, no
   * allocation. Returns the ECO_EVENT.* id (real-event ordinal + 1). Defensive
   * fallback to PesticideOutbreak if the weights sum to 0.
   */
  private pickEvent(): EcoEvent {
    const w = this.config.weights;
    let total = 0;
    for (let i = 0; i < ECO_EVENT_COUNT; i++) total += w[i] as number;
    if (total <= 0) return ECO_EVENT.PesticideOutbreak;
    let r = this.rng.int(total);
    for (let i = 0; i < ECO_EVENT_COUNT; i++) {
      r -= w[i] as number;
      if (r < 0) return (i + 1) as EcoEvent;
    }
    return ECO_EVENT_COUNT as EcoEvent; // unreachable for total>0; defensive.
  }

  /**
   * Onset cull. Two deterministic modes, selected by config.proportionalCull:
   *
   *  - DEFAULT (flat): kill the first floor(totalPopulation * pct / 100) wildlife
   *    in active-set order. This is the historical behaviour (byte-identical), but
   *    it biases the die-off toward the oldest/lowest ids.
   *  - PROPORTIONAL (opt-in): kill floor(speciesCount * pct / 100) of EACH species
   *    — a representative cross-section. The total is the sum of the per-species
   *    floors (≤ the flat floor, by floor sub-additivity).
   *
   * Both snapshot the active set FIRST (killWildlife despawns via swap-remove,
   * which would corrupt a live activeView walk — mirror organisms.ts/wildlife.ts)
   * and then walk that fixed-order snapshot, so victims are taken in a stable
   * order; no per-tick allocation beyond the preallocated scratch (+ the fixed
   * per-species quota for the proportional path).
   */
  private cullWildlife(pct: number): void {
    if (this.proportionalCull) {
      this.cullWildlifeProportional(pct);
    } else {
      this.cullWildlifeFlat(pct);
    }
  }

  /** Flat cull: first N wildlife in active-set order (N = floor(total*pct/100)). */
  private cullWildlifeFlat(pct: number): void {
    const wildlife = this.wildlife;
    const want = Math.floor((wildlife.population() * pct) / 100);
    if (want <= 0) return;

    const e = this.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    const scratch = this.scratch;
    for (let i = 0; i < n; i++) scratch[i] = active[i] as number;

    let killed = 0;
    for (let i = 0; i < n && killed < want; i++) {
      const id = scratch[i] as number;
      if (e.alive[id] === 0) continue;
      if ((e.kind[id] as number) !== KIND.Wildlife) continue;
      wildlife.killWildlife(id);
      killed++;
    }
  }

  /**
   * Proportional cull: kill floor(speciesCount * pct / 100) of EACH species (in
   * SPECIES.* order). Compute the fixed-size per-species quota, then make ONE pass
   * over the snapshot killing a unit only while its species still owes a kill
   * (decremented as we go). The snapshot preserves the active-set's deterministic
   * order, so WITHIN a species victims are taken in that fixed order.
   */
  private cullWildlifeProportional(pct: number): void {
    const wildlife = this.wildlife;
    // Per-species kill quota = floor(populationOf(sp) * pct / 100). Sum > 0?
    const quota = this.cullQuota;
    let want = 0;
    for (let sp = 0; sp < SPECIES_COUNT; sp++) {
      const q = Math.floor((wildlife.populationOf(sp as Species) * pct) / 100);
      quota[sp] = q;
      want += q;
    }
    if (want <= 0) return;

    const e = this.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    const scratch = this.scratch;
    for (let i = 0; i < n; i++) scratch[i] = active[i] as number;

    // ONE pass: kill a wildlife unit only while its species still owes a kill.
    for (let i = 0; i < n && want > 0; i++) {
      const id = scratch[i] as number;
      if (e.alive[id] === 0) continue;
      if ((e.kind[id] as number) !== KIND.Wildlife) continue;
      const sp = wildlife.species[id] as number;
      if ((quota[sp] as number) <= 0) continue;
      wildlife.killWildlife(id);
      quota[sp] = (quota[sp] as number) - 1;
      want--;
    }
  }

  /**
   * Onset burst: spawn `count` predators (Rat/Bird cycle) on passable tiles found
   * with the isolated rng (mirrors EcosystemSystem.pickPassable). Bounded retries
   * keep it deterministic + allocation-free. Skips a predator if no open tile.
   */
  private burstPredators(count: number): void {
    const grid = this.grid;
    for (let k = 0; k < count; k++) {
      const sp = BURST_CYCLE[this.burstCursor % BURST_CYCLE.length] as Species;
      this.burstCursor = (this.burstCursor + 1) % BURST_CYCLE.length;
      const tile = this.pickPassable();
      if (tile === NIL) continue;
      this.wildlife.spawnWildlife(sp, tile % grid.width, (tile / grid.width) | 0);
    }
  }

  /**
   * A passable tile (linear index), or NIL within the bounded budget. Mirrors
   * EcosystemSystem.pickPassable; draws from the ISOLATED event rng.
   */
  private pickPassable(): number {
    const grid = this.grid;
    let tries = 0;
    let x = 0;
    let y = 0;
    do {
      x = this.rng.int(grid.width);
      y = this.rng.int(grid.height);
      tries++;
    } while (!grid.isPassable(x, y) && tries < 64);
    if (!grid.isPassable(x, y)) return NIL;
    return grid.index(x, y);
  }

  // --- Read-only accessors (CLI / tests / hash) -------------------------------

  /** The currently active event (ECO_EVENT.None when idle). */
  activeEvent(): EcoEvent {
    return this.active;
  }
  /** Ticks the active event has left (0 when idle). */
  remainingTicks(): number {
    return this.remaining;
  }
  /** Ticks until the next event starts (only meaningful when idle). */
  ticksToNextEvent(): number {
    return this.ticksToNext;
  }
  /** Times the real event `ev` has fired (ev must be a real event, not None). */
  firedOf(ev: EcoEvent): number {
    const idx = (ev as number) - 1;
    if (idx < 0 || idx >= ECO_EVENT_COUNT) return 0;
    return this.firedByType[idx] as number;
  }
  /** Total events fired over the run. */
  get fired(): number {
    return this.eventsFiredTotal;
  }

  // Pass-through reads of the live modifier fields (CLI census / tests).
  foodRegenPct(): number {
    return this.mods.foodRegenPct;
  }
  wildlifeCapacityPct(): number {
    return this.mods.wildlifeCapacityPct;
  }
  geneDropPct(): number {
    return this.mods.geneDropPct;
  }
  organismDrain(): number {
    return this.mods.organismDrain;
  }
}
